import { createRouter, createWebHistory } from 'vue-router'
import * as Auth from '@/services/Auth'
import * as Language from '@/services/Language'
import { i18n } from '@/i18n/settings'
import { pageAddress, pageMeta, pageOfPath, pagePaths } from '@/i18n/pageMeta'
import DashboardView from '@/views/DashboardView.vue'
import EventListView from '@/views/events/EventListView.vue'
import EventDetailView from '@/views/events/EventDetailView.vue'
import EventTrackView from '@/views/events/EventTrackView.vue'
import HomeView from '@/views/HomeView.vue'
import LeaderboardView from '@/views/leaderboard/LeaderboardView.vue'
import LeaderboardTrackView from '@/views/leaderboard/LeaderboardTrackView.vue'
import LoginView from '@/views/auth/LoginView.vue'
import PlayView from '@/views/PlayView.vue'
import PlayerView from '@/views/PlayerView.vue'
import PostProductionView from '@/views/PostProductionView.vue'
import ProfileView from '@/views/ProfileView.vue'
import SettingsView from '@/views/SettingsView.vue'
import StandingsView from '@/views/StandingsView.vue'
import TrackEditorView from '@/views/TrackEditorView.vue'

// Steps above the backend pages, for the breadcrumb of the pages themselves (see AppBreadcrumb):
// the label is a translation key, the page that opens is a route. Only the steps a page hangs under
// are listed here — a section nobody is under needs no step.
const BACKEND_HOME = { key: 'gubola.title', to: { name: 'gubola' } }
const BACKEND_INCIDENTS = { key: 'gubola.incidents.title', to: { name: 'gubola_incidents' } }
const BACKEND_INCIDENT = { key: 'gubola.incidents.detailTitle', to: { name: 'gubola_incident' } }

/**
 * The pages of the site, one entry each, under the name every link of the app uses. The address is
 * not here: each language has its own (src/i18n/pageMeta/<language>.json), because the address is
 * what the visitor sees and the server has to answer it too.
 */
const pages = [
  {
    name: 'home',
    component: HomeView,
    // The landing page has nothing to say to somebody who is already inside: send them to play.
    meta: { redirectAuthenticatedTo: 'play' },
  },
  {
    name: 'test',
    component: () => import('@/views/TestView.vue'),
  },
  {
    name: 'about',
    // route level code-splitting: a separate chunk, loaded when the route is visited
    component: () => import('@/views/AboutView.vue'),
  },
  {
    name: 'privacy',
    component: () => import('@/views/PrivacyView.vue'),
  },
  {
    name: 'info',
    component: () => import('@/views/InfoView.vue'),
  },
  {
    name: 'dashboard',
    component: DashboardView,
    meta: { requiresAuth: true }, // Only accessible when logged in
  },
  {
    name: 'profile',
    component: ProfileView,
    meta: { requiresAuth: true },
  },
  {
    name: 'settings',
    component: SettingsView,
    meta: { requiresAuth: true },
  },
  {
    // Change of the account email: the account goes back to unverified until the new address is
    // activated (see GamePlayButton).
    name: 'change_email',
    component: () => import('@/views/ChangeEmailView.vue'),
    meta: { requiresAuth: true },
  },

  // Backend ("gubola"): one permission per page, the same one that guards the endpoint behind it
  // (see doc/gubola.md). The home is the area's door: whoever can manage something inside it gets in.
  {
    name: 'gubola',
    component: () => import('@/views/gubola/GubolaHomeView.vue'),
    meta: { requiresAuth: true, permission: 'backend.access' },
  },
  {
    name: 'gubola_users',
    component: () => import('@/views/gubola/GubolaUsersView.vue'),
    meta: { requiresAuth: true, permission: 'users.manage', breadcrumb: [BACKEND_HOME] },
  },
  {
    name: 'gubola_permissions',
    component: () => import('@/views/gubola/GubolaPermissionsView.vue'),
    meta: { requiresAuth: true, permission: 'roles.manage', breadcrumb: [BACKEND_HOME] },
  },
  {
    name: 'gubola_incidents',
    component: () => import('@/views/gubola/GubolaIncidentsView.vue'),
    meta: { requiresAuth: true, permission: 'incidents.manage', breadcrumb: [BACKEND_HOME] },
  },
  {
    name: 'gubola_incident',
    component: () => import('@/views/gubola/GubolaIncidentView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'incidents.manage',
      breadcrumb: [BACKEND_HOME, BACKEND_INCIDENTS],
    },
  },
  {
    name: 'gubola_info',
    component: () => import('@/views/gubola/GubolaInfoView.vue'),
    meta: { requiresAuth: true, permission: 'backend.access', breadcrumb: [BACKEND_HOME] },
  },
  {
    name: 'gubola_maintenance',
    component: () => import('@/views/gubola/GubolaMaintenanceView.vue'),
    meta: { requiresAuth: true, permission: 'maintenance.manage', breadcrumb: [BACKEND_HOME] },
  },
  {
    name: 'gubola_news',
    component: () => import('@/views/gubola/GubolaNewsView.vue'),
    meta: { requiresAuth: true, permission: 'news.manage', breadcrumb: [BACKEND_HOME] },
  },

  // Auth
  {
    name: 'login',
    component: LoginView,
  },
  {
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
  },
  {
    // Asks for the link sent by email
    name: 'forgot_password',
    component: () => import('@/views/ResetPasswordView.vue'),
  },
  {
    // The page of that link: its token and the address of the account come in the query string
    name: 'reset_password',
    component: () => import('@/views/ResetPasswordConfirmView.vue'),
  },

  // Game
  {
    name: 'play',
    component: PlayView,
    meta: { requiresAuth: true },
  },
  {
    name: 'track_editor',
    component: TrackEditorView,
    meta: { requiresAuth: true },
  },
  {
    // The tiles of a picture made elsewhere: the same pyramid the editor paints, cut from a file
    // (see PostProductionView).
    name: 'post_production',
    component: PostProductionView,
    meta: { requiresAuth: true },
  },

  // Leaderboard
  {
    name: 'leaderboard',
    component: LeaderboardView,
  },
  {
    name: 'leaderboard_track',
    component: LeaderboardTrackView,
  },
  {
    name: 'leaderboard_track_car',
    component: LeaderboardTrackView,
  },

  // Events
  {
    name: 'events',
    component: EventListView,
  },
  {
    name: 'event_detail',
    component: EventDetailView,
  },
  {
    // One track of an event: everybody's results there
    name: 'event_track',
    component: EventTrackView,
  },

  // Players
  {
    // Public profile, addressed by the player name (unique, so it works as username)
    name: 'player',
    component: PlayerView,
  },

  // Tracks
  {
    // One track: picture, country and what is written about it
    name: 'track',
    component: () => import('@/views/TrackView.vue'),
  },

  // News (public): the archive of the automatic and manual news, linked from the footer
  {
    name: 'news',
    component: () => import('@/views/NewsView.vue'),
  },

  // Standings
  {
    name: 'standings',
    component: StandingsView,
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [],
  /**
   * A link to a section of a page (`/info#season-ranking`, the "?" of the season track table)
   * lands on that section. Everything else keeps the browser behaviour it had.
   */
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return waitForElement(to.hash).then(found => (found ? { el: to.hash, top: 80 } : undefined))
    }

    if (savedPosition) {
      return savedPosition
    }
  },
})

/**
 * Waits for the element a hash points at: pages are lazy chunks, so when the address with the hash
 * is opened straight from a link the section is not mounted yet.
 */
async function waitForElement(selector, attempts = 40)
{
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (document.querySelector(selector)) {
      return true
    }

    await new Promise(resolve => requestAnimationFrame(resolve))
  }

  return false
}

// An address with no language in it (the root, a link from before) matches no page: this is what makes the
// router look at it at all, so that the guard below can move it to the visitor's language. It is written
// once and never rendered: the guard always sends it somewhere that has a page.
router.addRoute({
  path: '/:pathMatch(.*)*',
  name: 'bare',
  component: () => import('@/views/PageNotFound.vue'),
})

let installed = null
let removers = []

/**
 * Routes of a language: every page at its address in that language, behind the language itself
 * (/en/events, /it/eventi). The names do not change with the language, so every link of the app goes
 * on working and only the address follows the visitor.
 */
export function installRoutes(language)
{
  if (installed === language) {
    return
  }

  for (const remove of removers) {
    remove()
  }

  removers = []
  const addresses = pagePaths(language)

  for (const page of pages) {
    if (!addresses[page.name]) {
      console.error(`[router] ${page.name} has no address for "${language}"`)
      continue
    }

    removers.push(router.addRoute({ ...page, path: withLanguage(language, addresses[page.name]) }))
  }

  removers.push(router.addRoute({
    path: `/${language}/:pathMatch(.*)*`,
    name: 'notfound',
    component: () => import('@/views/PageNotFound.vue'),
  }))

  installed = language
}

/**
 * Address of a page in a language: the language is always in front of it (/en/events, /it/eventi) and the
 * home of a language is the language alone. The server writes the same ones (see address in meta.php).
 */
function withLanguage(language, address)
{
  return address === '/' ? `/${language}` : `/${language}${address}`
}

/**
 * The language is always written in the address, so a page is one page and a shared link keeps the
 * language it was shared in. The router only has the addresses of the language in use (see
 * `installRoutes`), so an address written in another one — an old link, or the page a login was
 * interrupted on — is read by the page it names and written again in that language.
 *
 * A guard cannot be started before the router is installed — vue-router reads the address when it is
 * created and cancels a navigation begun earlier — so the move waits for the first navigation the router
 * does on its own: the one of the address as it arrived.
 */
function settleAddress(to)
{
  const written = Language.languageOfPath(to.path)
  const language = Language.applied()

  if (written === language) {
    return
  }

  const path = written === null ? to.path : (to.path.slice(written.length + 1) || '/')
  const page = pageOfPath(written ?? Language.defaultLanguage(), path)
  const address = page === null ? null : pageAddress(language, page.name, page.params)

  // An address that names no page is left as it is when it carries a language of the site: the
  // catch-all answers it with the 404 page.
  if (address === null && written !== null) {
    return
  }

  return {
    path: withLanguage(language, address ?? to.path),
    query: to.query,
    hash: to.hash,
    replace: true,
  }
}

/**
 * The address follows the language: a visitor who chooses one (the selector of the pages, or the
 * preference of the account at login) gets the page it is on written in the new one. The names do not
 * change with the language, so the page follows.
 *
 * The language of a page is the one written in it (see services/Language and meta.php), so a link keeps
 * the language it was shared in; `settleAddress` writes in the language in use an address that carries
 * another one.
 */
export function followLanguage(language)
{
  if (Language.languageOfAddress() === language) {
    return
  }

  const page = router.currentRoute.value

  installRoutes(language)

  // A page nobody knows (the 404, an address that is not the site's, an address with no language yet):
  // there is nothing to translate.
  if (!page.name || page.name === 'notfound' || page.name === 'bare') {
    return
  }

  router.replace({
    name: page.name,
    params: page.params,
    query: page.query,
    hash: page.hash,
  }).catch(() => {})
}

// A language chosen while the app runs (the selector of the pages, or the preference of the account
// at login) takes the page it is on along, in the new language.
Language.onApply(followLanguage)

// An address without a language is moved to the visitor's own one before anything else looks at it.
router.beforeEach(settleAddress)

// Navigation guard
router.beforeEach((to, from, next) => {
  const isAuthenticated = Auth.isAuthenticated()
  
  // Routes that require the user to be a guest (not logged in)
  if (to.meta.requiresGuest && isAuthenticated) {
    next({ name: 'home' })
    return
  }

  // Routes a logged in user skips (e.g. the home landing page)
  if (to.meta.redirectAuthenticatedTo && isAuthenticated) {
    // The query string travels with the redirect: the result of the email verification is
    // announced there (see VerifyEmailAlert).
    next({ name: to.meta.redirectAuthenticatedTo, query: to.query })
    return
  }
  
  // Routes that require authentication
  if (to.meta.requiresAuth && !isAuthenticated) {
    // The page the user was heading to travels with the login, which sends them back there: without
    // it, a link to a page (the one at the bottom of a notification email, for example) would end
    // on the home page of a logged user.
    next({ name: 'login', query: { redirectFrom: to.fullPath } })
    return
  }

  // Routes that require a permission (or an ability derived from them, e.g. "backend.access").
  // This only keeps the page out of the way: the server refuses the calls on its own, so a user who
  // types the address gets an empty page and not data he is not allowed to see.
  if (to.meta.permission && !Auth.can(to.meta.permission)) {
    next({ name: 'home' })
    return
  }

  next()
})

// The title of the tab, from the same texts meta.php puts in the <head> of the page (see
// i18n/pageMeta.js): the crawlers read them from the server, the browser from here.
router.beforeEach((to) => {
  const title = pageMeta(i18n.global.locale.value, to.name, to.params).title

  if (title) {
    document.title = title
  }
})

router.afterEach((to) => {
  if (typeof window !== 'undefined' && window.scrollTo) {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }
})

export default router
